import './index.scss'
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout, Input } from 'antd'
import { v4 as uuid } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import actionTypes from '../../../constants/actionTypes'
import {
  setEditorWatermark,
  setEditorGeoItemList,
  setEditorCascadeItemsList,
  fetchWatermarkItemsList
} from '../../../actions/watermark'
import {
  MUST_FILLED_CLEAR_TYPES,
  BASE_ID,
  WATERMARK_CONFIG,
  WM_STORAGE_KEYS
} from '../../../constants/watermark'
import parseQueryString from '../../../utils/parseQueryString'

import ControllerPanel from './components/ControllerPanel'
import FormPanel from './components/FormPanel'
import WatermarkPreview from './components/WatermarkPreview'
import { store } from '../../../app/store'
import sensors from '../../../utils/sensors'
import { coordinateBeforeEditAndSubmit } from './components/BatchImportGeoData/lib'
// import desktopLib from '../../../utils/desktopLib'
import { subWatermarkText } from '../../../utils/utils'
import { EyeFilled, LeftOutlined, RightOutlined } from '@ant-design/icons'
import AntdProvider from '@/components/AntdProvider'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

const mergeItems = (presetItems, onlineItems) => {
  const isVip = store.getState().group.isVip
  // 深拷贝预设条目：下方会就地改写 item.contentEdit / item.content（mergeItems 既有逻辑）。
  // presetItems 来自 WATERMARK_CONFIG 模块常量，编辑器 seed 时其引用被塞进 Redux 并被
  // RTK/Immer 深冻结；若直接改写会抛 "Cannot add property contentEdit, object is not
  // extensible" → 二次编辑失败。深拷贝后改写自有副本，等价基准版 Immutable.fromJS 的深拷贝语义。
  let items = JSON.parse(JSON.stringify(presetItems))
  // merge
  items.forEach((item, index) => {
    const groupWatermarkItem = onlineItems.find((i) => i.id === item.id)
    if (groupWatermarkItem) {
      items[index] = groupWatermarkItem
    }
  })

  // 预设内容不包含自定义项目，如果有自定义项目，加入items
  const customItems = onlineItems.filter(
    (item) => item.userCustom && item.id !== 13
  ) // ID_13拍摄人，历史遗留问题，是自定义条目，允许删除，但Web端作为普通条目处理
  items = items.concat(customItems)

  items.forEach((item, index) => {
    // 如果为旧版本，将item转换为新版本
    if (typeof item.contentEdit === 'undefined') {
      if (item.editType === 0 || item.editType === 1) {
        items[index].contentEdit = {
          editType: 1,
          placeholder: '',
          itemsList: []
        }
      }
      if (item.editType === 2 || item.editType === 3) {
        items[index].contentEdit = {
          editType: 0,
          placeholder: item.content,
          itemsList: []
        }
        items[index].content = ''
      }
    }

    // v1.250.0: 如果VIP团队且已开启必填项，但未设置过clearTextType，将clearTextType设置为每5分钟清空
    // 340 版本变更：必填项改为限免功能，无论是否vip用户都可以使用，因此注释掉 isVip 的判断条件
    if (
      // isVip &&
      item?.contentEdit?.isMustFilled &&
      item?.contentEdit?.clearTextType === undefined
    ) {
      items[index].contentEdit.clearTextType =
        MUST_FILLED_CLEAR_TYPES.FIVE_MINUTES
    }
  })
  return items
}

// 线上APP创建的水印，不会将为开启的item写入水印内容，需要与预设内容进行merge
const processingAppWatermark = (watermarkConfig, appWatermark) => {
  if (!watermarkConfig) {
    return appWatermark
  }

  const presetItems = watermarkConfig.watermark.items

  // 合并items
  appWatermark.items = mergeItems(presetItems, appWatermark.items)
  // 升级为最新版本
  appWatermark.version = watermarkConfig.watermark.version
  // 如果logo字段被删除，添加上默认值
  if (watermarkConfig.logo && appWatermark.logo === undefined) {
    appWatermark.logo = watermarkConfig.watermark.logo
  }
  return appWatermark
}

function WatermarkEditor() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // local state
  const [webNotSupportItems, setWebNotSupportItems] = useState([])
  const [isPreviewHidden, setIsPreviewHidden] = useState(false)
  const [activeTabKey, setActiveTabKey] = useState('content')

  // Redux state
  const user = useAppSelector((state) => state.main.user)
  const groupId = useAppSelector((state) => state.group.groupId)
  const groupName = useAppSelector((state) => state.group.groupName)
  const watermark = useAppSelector((state) => state.editor.watermark)
  const watermarkList = useAppSelector((state) => state.watermark.watermarkList)
  const isVip = useAppSelector((state) => state.group.isVip)

  // 转换 itemsList 为 geoItemsList
  const transformItemsListToGeoItemsList = useCallback(
    (
      watermarkID: string,
      itemsList: Array<{
        itemId: string | number
        records: Array<{
          content: string
          uploadLocation?: Array<{ lat: string; lng: string }>
        }>
      }>
    ) => {
      const result: Array<{
        watermarkID: string
        itemID: string | number
        content: string
        uploadLocation: Array<{ lat: string; lng: string }>
      }> = []
      itemsList.forEach((item) => {
        item.records.forEach((record) => {
          result.push({
            watermarkID: watermarkID,
            itemID: item.itemId,
            content: record.content,
            uploadLocation: record.uploadLocation || []
          })
        })
      })
      return result
    },
    []
  )

  // 获取水印条目列表
  const handleFetchWatermarkItemsList = useCallback(
    (watermarkID) => {
      return dispatch(
        fetchWatermarkItemsList({
          userID: user?.user_id,
          groupID: groupId,
          watermarkID: watermarkID
        })
      ).then((data) => {
        if (data.status === 0) {
          dispatch(setEditorCascadeItemsList(data.multipleItems || []))
          return data
        }
        return null
      })
    },
    [dispatch, user?.user_id, groupId]
  )

  // 缓存 web 端不支持的条目
  const handleCacheWebNotSupportItems = useCallback(
    (content, watermarkConfig) => {
      const presetItems = watermarkConfig.watermark.items
      const onlineItems = content.items
      // web端暂不支持的条目，但不包含自定义条目，比如：成员信息
      const notSupportItems = onlineItems.filter(
        (item) =>
          !item.userCustom &&
          presetItems.findIndex((i) => i.id === item.id) === -1
      )
      setWebNotSupportItems(notSupportItems)
    },
    []
  )

  // componentDidMount
  useEffect(() => {
    const { action, base_id, watermark_id, is_draft } = parseQueryString()
    const watermarkConfig = base_id
      ? WATERMARK_CONFIG[base_id as keyof typeof WATERMARK_CONFIG]
      : undefined
    const groupWatermark = watermarkList.find(
      (wm: any) => wm.id === watermark_id
    )
    const wmStorageStr = localStorage.getItem(WM_STORAGE_KEYS.WM)
    const storageWatermarkRaw = wmStorageStr ? JSON.parse(wmStorageStr) : null
    const storageWatermark = storageWatermarkRaw
      ? storageWatermarkRaw.find((wm) => wm.id === watermark_id)
      : null

    if (action === 'new' && base_id && watermarkConfig) {
      // 深拷贝预设：避免把 WATERMARK_CONFIG 模块常量的嵌套 items 引用塞进 Redux，
      // 被 Immer 深冻结后污染常量、导致后续 mergeItems 改写预设条目抛错（二次编辑失败）。
      const wm: any = JSON.parse(JSON.stringify(watermarkConfig.watermark))
      wm.id = uuid()
      wm.groupId = groupId
      wm.createTime = String(new Date().getTime())
      wm.update_time = wm.createTime
      dispatch(setEditorWatermark(wm))
    } else if (action === 'modify' && watermark_id) {
      if (is_draft === '1' && storageWatermark) {
        let content = JSON.parse(storageWatermark.content)
        handleCacheWebNotSupportItems(
          content,
          WATERMARK_CONFIG[storageWatermark.baseID]
        )
        content = processingAppWatermark(
          WATERMARK_CONFIG[content.base_id],
          content
        )
        // 根据plainItemsList，更新geoItemsList, 保证geoItemsList中的条目都是已有的条目
        const geoItemsList = coordinateBeforeEditAndSubmit(
          content,
          storageWatermark.itemsList || []
        )
        dispatch(setEditorWatermark(content))
        dispatch(setEditorGeoItemList(geoItemsList))
      } else if (groupWatermark) {
        let content = JSON.parse(groupWatermark.content)
        handleCacheWebNotSupportItems(
          content,
          WATERMARK_CONFIG[groupWatermark.baseID]
        )
        content = processingAppWatermark(
          WATERMARK_CONFIG[groupWatermark.baseID],
          content
        )
        // 090 不再使用watermark中的itemsList, 使用单独接口请求的itemsList
        dispatch(setEditorWatermark(content))

        handleFetchWatermarkItemsList(groupWatermark.id).then((data) => {
          if (data) {
            // 090 将接口下发的items转换为geoItemsList
            const geoItemsList = transformItemsListToGeoItemsList(
              groupWatermark.id,
              data.items
            )
            // 090 根据plainItemsList，更新geoItemsList, 保证geoItemsList中的条目都是已有的条目
            const coordinatedGeoItemsList = coordinateBeforeEditAndSubmit(
              content,
              geoItemsList
            )
            dispatch(setEditorGeoItemList(coordinatedGeoItemsList as any))
          }
        })
      } else {
        navigate('/watermark/list', { replace: true })
      }
    } else {
      navigate('/watermark/list', { replace: true })
    }

    // eslint-disable-next-line no-undef
    if (!__DEBUG__) {
      window.onbeforeunload = (e) => {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    // componentWillUnmount
    return () => {
      dispatch({ type: actionTypes.CLEAR_EDITOR })
      window.onbeforeunload = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // 点击事件埋点
  const handleClickEvent = useCallback(
    (clickItem: string, itemID: string | number = '') => {
      const { action } = parseQueryString()

      // 获取主题色值
      const themeColor = watermark?.theme?.color || ''

      // 获取已开启的条目ID列表
      const usedItemList =
        watermark?.items
          ?.filter((item) => item?.switchStatus)
          ?.map((item) => item?.id) || []

      // 获取水印ID（创建时就有ID，直接上报）
      const watermarkID = watermark?.id || ''
      const params = {
        clickItem,
        tabName:
          clickItem === 'tabEdit'
            ? 'watermarkEdit'
            : clickItem === 'tabStyle'
              ? 'style'
              : activeTabKey === 'content'
                ? 'watermarkEdit'
                : 'style',
        ThemeColor: themeColor,
        UsedItem: usedItemList,
        WatermarkID: watermarkID,
        itemID,
        baseID: watermark?.base_id,
        isGroupVIP: isVip,
        pageType: action === 'new' ? 'creatWatermark' : 'modifyWatermark'
      }
      console.log('web_click_page_workgroup_watermark_edit', params)
      sensors.track('web_click_page_workgroup_watermark_edit', params)
    },
    [watermark, isVip, activeTabKey]
  )

  // Tab 切换
  const handleTabChange = useCallback(
    (activeKey) => {
      setActiveTabKey(activeKey)
      handleClickEvent(activeKey === 'content' ? 'tabEdit' : 'tabStyle')
    },
    [handleClickEvent]
  )

  // 点击预览隐藏/显示
  const handleClickPreviewHide = useCallback(() => {
    setIsPreviewHidden((prev) => !prev)
  }, [])

  const baseId = watermark?.base_id
  const isId20 = baseId === BASE_ID.ID_20

  return (
    <div
      className={`watermark-editor-container ${
        !isPreviewHidden && isId20
          ? 'watermark-editor-container-preview-show'
          : ''
      }`}
    >
      <Layout className="WatermarkEditor">
        <PageHeader
          className="Header"
          ghost={true}
          title={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                color: '#444'
              }}
            >
              <LeftOutlined
                onClick={() => navigate('/watermark/list', { replace: true })}
              />
              <Input
                style={{
                  // height: '42px',
                  width: '300px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '500'
                }}
                placeholder="请输入水印模板名称..."
                value={watermark?.name || ''}
                onChange={(e) => {
                  dispatch(
                    setEditorWatermark({
                      ...watermark,
                      name: subWatermarkText(e.target.value, 12)
                    })
                  )
                }}
              />
            </div>
          }
        />
        <Content className="content">
          <ControllerPanel
            clickEvent={handleClickEvent}
            onTabChange={handleTabChange}
          />
          <FormPanel
            webNotSupportItems={webNotSupportItems}
            clickEvent={handleClickEvent}
            activeTabKey={activeTabKey}
            onTabChange={handleTabChange}
          />
          {isId20 && (
            <>
              {isPreviewHidden ? (
                <div
                  className="watermark-preview-collapsed"
                  onClick={handleClickPreviewHide}
                >
                  <EyeFilled />
                  <span>展开预览</span>
                </div>
              ) : (
                <div className="watermark-preview-container">
                  <div className="watermark-preview-header">
                    <div className="watermark-preview-header-left">
                      <RightOutlined onClick={handleClickPreviewHide} />
                    </div>
                    <div className="watermark-preview-header-title">
                      <EyeFilled />
                      &nbsp;{'手机预览'}
                    </div>
                  </div>
                  <WatermarkPreview
                    groupName={groupName}
                    clickEvent={handleClickEvent}
                  />
                </div>
              )}
            </>
          )}
        </Content>
      </Layout>
    </div>
  )
}

const WatermarkEditorPage = () => (
  <AntdProvider>
    <WatermarkEditor />
  </AntdProvider>
)

export default WatermarkEditorPage
